"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorPicker = exports.getColorValue = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
require("@itwin/itwinui-css/css/color-picker.css");
const index_js_1 = require("../utils/index.js");
const classnames_1 = __importDefault(require("classnames"));
const ColorPickerContext_js_1 = require("./ColorPickerContext.js");
const getColorValue = (color) => {
    if (color instanceof index_js_1.ColorValue) {
        return color;
    }
    return index_js_1.ColorValue.create(color);
};
exports.getColorValue = getColorValue;
/**
 * ColorPicker to display color builder options, color inputs, and a palette of ColorSwatches.
 * @example
 * <ColorPicker onChangeComplete={() => {}} selectedColor={activeColor}>
 *   <ColorPalette colors={['#FFFFFF', '#5A6973']} />
 * </ColorPicker>
 * @example
 * <ColorPicker onChangeComplete={() => {}} selectedColor={activeColor}>
 *   <ColorBuilder />
 *   <ColorInputPanel defaultColorFormat='hsl' />
 *   <ColorPalette label='Saved colors' colors={['#FFFFFF', '#5A6973']} />
 * </ColorPicker>
 */
const ColorPicker = (props) => {
    const { children, className, selectedColor, onChange, onChangeComplete, setFocus = false, showAlpha = false, ...rest } = props;
    (0, index_js_1.useTheme)();
    const ref = React.useRef(null);
    // set focus on the first tabbable element
    React.useEffect(() => {
        if (ref.current && setFocus) {
            const tabbableElements = (0, index_js_1.getTabbableElements)(ref.current);
            tabbableElements[0].focus();
        }
    }, [setFocus]);
    const inColor = React.useMemo(() => (0, exports.getColorValue)(selectedColor), [selectedColor]);
    const activeColorTbgr = React.useRef(inColor.toTbgr());
    const [activeColor, setActiveColor] = React.useState(inColor); // Color of colorDot or active ColorSwatch
    React.useEffect(() => {
        setActiveColor(inColor);
    }, [inColor]);
    const [hsvColor, setHsvColor] = React.useState(() => activeColor.toHsvColor());
    // The following code is used to preserve the Hue after initial mount. If the current HSV value produces the same rgb value
    // as the selectedColor prop then leave the HSV color unchanged. This prevents the jumping of HUE as the s/v values are changed
    // by user moving the pointer.
    React.useEffect(() => {
        if (inColor.toTbgr() !== activeColorTbgr.current) {
            activeColorTbgr.current = inColor.toTbgr();
            setHsvColor(inColor.toHsvColor());
        }
    }, [inColor]);
    const applyHsvColorChange = React.useCallback((newColor, selectionChanged, newColorValue) => {
        // save the HSV values
        setHsvColor(newColor);
        const newActiveColor = newColorValue !== null && newColorValue !== void 0 ? newColorValue : index_js_1.ColorValue.create(newColor);
        // Only update selected color when dragging is done
        if (selectionChanged) {
            onChangeComplete === null || onChangeComplete === void 0 ? void 0 : onChangeComplete(newActiveColor);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(newActiveColor);
        }
        activeColorTbgr.current = newActiveColor.toTbgr();
        // this converts it to store in tbgr
        setActiveColor(newActiveColor);
    }, [onChange, onChangeComplete]);
    return (React.createElement("div", { className: (0, classnames_1.default)('iui-color-picker', className), ref: ref, ...rest },
        React.createElement(ColorPickerContext_js_1.ColorPickerContext.Provider, { value: {
                activeColor,
                setActiveColor,
                hsvColor,
                applyHsvColorChange,
                onChangeComplete,
                showAlpha,
            } }, children)));
};
exports.ColorPicker = ColorPicker;
exports.default = exports.ColorPicker;
